import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from "react";

import ReactEcharts from "echarts-for-react";
import { InputGroup } from "react-bootstrap";
import { isMobile } from "react-device-detect";

import { format } from "date-fns";
import * as XLSX from "xlsx";

import AgGridReactEx from "@app/components/AgGridReactEx";
import { toast } from "react-toastify";

import utility from "@app/lib/utility";

const RATE_LIST = [
    {
        RATE: "A",
        NAME: "우수",
        COLOR: "#099AE5",
    },
    {
        RATE: "B",
        NAME: "보통",
        COLOR: "#0CD02C",
    },
    {
        RATE: "C",
        NAME: "미흡",
        COLOR: "#FFC500",
    },
    {
        RATE: "D",
        NAME: "경고",
        COLOR: "#FF4A1C",
    },
];

const PieRateTotal = ({ rateTotal, isTotal, height = 100, fontSizeNormal = 20, fontSizeEmphasis = 25 }) => {
    const optionBase = {
        series: [
            {
                name: isTotal ? "전 국" : "지 사",
                type: "pie",
                radius: ["30%", "30%"],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    position: "center",
                    formatter: "{a}",
                    color: "#333",
                    fontSize: "20",
                },
                emphasis: {
                    label: {
                        show: true,
                        formatter: (params) => {
                            return `${params.seriesName}\n${params.value ? params.value.toLocaleString() : params.value}`;
                        },
                        color: "#333",
                    },
                },
                data: [0],
                color: RATE_LIST[0].COLOR,
            },
            {
                name: "평가",
                type: "pie",
                radius: ["30%", "90%"],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    position: "inner",
                    formatter: (params) => {
                        return `${params.percent ? params.percent.toFixed(1) : params.percent}%`;
                    },
                    color: "#eee",
                    fontSize: "20",
                },
                emphasis: {
                    label: {
                        show: true,
                        formatter: (params) => {
                            return `${params.name}\n${params.percent ? params.percent.toFixed(1) : params.percent}%`;
                        },
                        color: "#555",
                    },
                },
                sort: "none",
                color: RATE_LIST.map((r) => r.COLOR),
                data: RATE_LIST.map((r) => ({ name: r.NAME, value: 0 })),
            },
        ],
    };
    const [option, setOption] = useState(optionBase);

    useEffect(() => {
        if (rateTotal) {
            let total = 0;
            const data = [];
            const _optionBase = { ...optionBase };
            for (let i = 0; i < RATE_LIST.length; i += 1) {
                const rate = rateTotal.find((r) => r.rate === RATE_LIST[i].RATE);
                data.push({ name: RATE_LIST[i].NAME, value: rate ? rate.count : 0 });
                total += rate ? rate.count : 0;
            }
            _optionBase.series[0].data = [total];
            _optionBase.series[1].data = data;
            setOption(_optionBase);
        }
        return () => {};
    }, [rateTotal]);

    return <ReactEcharts theme="macarons" option={option} style={{ height: `${height}px` }} />;
};

const PieRate = ({ rate, value, type, height = 50, fontSizeNormal = 14, fontSizeEmphasis = 18, onClick }) => {
    useEffect(() => {
        return () => {};
    }, [rate, value]);
    const RATE = RATE_LIST.find((r) => r.RATE === rate.rate);
    const option = {
        tooltip: { show: false },
        legend: { show: false },
        series: [
            {
                name: "평가",
                type: "pie",
                radius: ["70%", "75%"],
                avoidLabelOverlap: false,
                label: {
                    show: true,
                    position: "center",
                    formatter: (params) => {
                        return `{title|${params.name}}\n\n{value|${params.value ? params.value.toLocaleString() : params.value}}`;
                    },
                    rich: {
                        title: { color: "#333", fontSize: fontSizeNormal, align: "center" },
                        value: { color: RATE.COLOR, fontSize: fontSizeEmphasis, align: "center" },
                    },
                },
                emphasis: {
                    label: {
                        show: true,
                        formatter: (params) => {
                            return `{title|${params.name}}\n\n{value|${params.value ? params.value.toLocaleString() : params.value}}`;
                        },
                        rich: {
                            title: { color: "#333", fontSize: fontSizeEmphasis, align: "center" },
                            value: { color: RATE.COLOR, fontSize: fontSizeEmphasis, align: "center" },
                        },
                    },
                },
                data: [{ name: RATE.NAME, value: value }],
                color: [RATE.COLOR],
            },
        ],
    };

    const onEvents = {
        click: (params) => {
            if (onClick) {
                onClick(type, rate.rate);
            }
        },
    };

    return <ReactEcharts theme="macarons" option={option} style={{ height: `${height}px` }} onEvents={onEvents} />;
};

const PieRateList = ({ rateList, type, height = 50, fontSizeNormal = 14, fontSizeEmphasis = 18, onClick }) => {
    const [rateListAll, setRateListAll] = useState(
        RATE_LIST.map((r) => ({
            rate: r.RATE,
            name: r.NAME,
            value: 0,
        }))
    );
    useEffect(() => {
        if (rateList) {
            const _rateListAll = [...rateListAll];
            for (let i = 0; i < _rateListAll.length; i += 1) {
                const rate = rateList.find((r) => r.rate === _rateListAll[i].rate);
                _rateListAll[i].value = rate ? rate.count : 0;
            }
            setRateListAll(_rateListAll);
        }
        return () => {};
    }, [rateList]);

    return (
        <div className="row">
            {rateListAll &&
                rateListAll.map((r) => (
                    <div className="col" key={`rate-${type}-${r.rate}`}>
                        <PieRate rate={r} value={r.value} type={type} height={height} fontSizeNormal={fontSizeNormal} fontSizeEmphasis={fontSizeEmphasis} onClick={onClick} />
                    </div>
                ))}
        </div>
    );
};

const SearchField = forwardRef(({ defaultValue = "", handleKeyPress }, ref) => {
    const [value, setValue] = useState(defaultValue);
    useEffect(() => {
        return () => {};
    }, [defaultValue]);
    useImperativeHandle(ref, () => ({
        getValue() {
            return value;
        },
        setValue(value) {
            setValue(value);
        },
    }));

    return (
        <>
            <input
                className="form-control"
                type="text"
                value={value}
                placeholder="원명|계정|반 명 검색"
                onChange={(e) => {
                    setValue(e.target.value);
                }}
                onKeyPress={(e) => handleKeyPress(e)}
            />
        </>
    );
});

const ClassList = forwardRef((props, ref) => {
    const defaultSearchParam = {
        page: 0,
        type: "",
        rate: "",
        value: "",
        sort_col: props.selectedBranch ? "nurseryName" : "branchName",
        sort: "asc",
    };
    useEffect(() => {
        if (props.data) {
            applyData(searchParam, props.data.list);
        }
        return () => {};
    }, [props.data, props.selectedBranch]);
    useImperativeHandle(ref, () => ({
        setSearchParam(type, rate) {
            const _searchParam = { ...searchParam };
            _searchParam.type = type;
            _searchParam.rate = rate;
            applyData(_searchParam, props.data.list);
        },
    }));

    const [tableData, setTableData] = useState(false);
    const [searchParam, setSearchParam] = useState(defaultSearchParam);
    const [searchFilter, setSearchFilter] = useState({
        all: true,
    });
    const searchInputRef = useRef();
    const onChangeFilter = (e) => {
        if (!searchFilter.all) {
            if (searchInputRef && searchInputRef.current) {
                searchInputRef.current.setValue("");
            }
            applyData(defaultSearchParam, props.data.list);
        }
    };
    let gridApi = null;
    const onGridReady = (params) => {
        gridApi = params.api;
        const sortModel = [{ colId: searchParam.sort_col, sort: searchParam.sort }];
        gridApi.setSortModel(sortModel);
    };
    const onTableDataChanged = () => {};
    const onSortChanged = (params) => {
        const sortState = params.api.getSortModel();
        if (sortState.length != 0) {
            let searchParamNew = { ...searchParam };
            searchParamNew.sort_col = sortState[0].colId;
            searchParamNew.sort = sortState[0].sort;
            setSearchParam(searchParamNew);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            onSearch();
        }
    };
    const onSearch = () => {
        if (searchInputRef && searchInputRef.current) {
            searchParam.value = searchInputRef.current.getValue();
        }
        applyData(searchParam, props.data.list);
    };
    const applyData = (searchParam, list) => {
        var tableData = listSearch(searchParam, list);

        setTableData(tableData);
        setSearchParam(searchParam);
        const _searchFilter = { ...searchFilter };
        if (!searchParam.type && !searchParam.rate && !searchParam.value) {
            _searchFilter.all = true;
        } else {
            _searchFilter.all = false;
        }
        setSearchFilter(_searchFilter);
        if (gridApi) {
            const sortModel = [{ colId: searchParam.sort_col, sort: searchParam.sort }];
            gridApi.setSortModel(sortModel);
        }
    };
    const searchField = ["nurseryName", "className", "userId"];
    const listSearch = (searchParam, list) => {
        let localList = list;
        let tableDataNew = [];
        if (!searchParam.type && !searchParam.rate && !searchParam.value) {
            tableDataNew = localList;
        } else {
            localList.forEach((item) => {
                searchField.forEach((field) => {
                    if (searchParam.type && item[`${searchParam.type}Rate`] != searchParam.rate) {
                        return;
                    }
                    if (item[field] && item[field].indexOf(searchParam.value) >= 0) {
                        if (!props.selectedBranch || (props.selectedBranch && item.branchId == props.selectedBranch.id)) {
                            if (tableDataNew.findIndex((d) => d.branchId == item.branchId && d.nurseryId == item.nurseryId && d.classId == item.classId) == -1) {
                                tableDataNew.push(item);
                            }
                        }
                    }
                });
            });
        }
        return tableDataNew;
    };
    const getFormattedLast = (last) => {
        if (last != "-") {
            return last.substring(0, 4) + "/" + last.substring(4, 6) + "/" + last.substring(6);
        }
        return last;
    };
    const getRateName = (rate) => {
        const finded = RATE_LIST.find((r) => r.RATE == rate);
        return finded ? finded.NAME : "-";
    };
    const saveTableXlsx = () => {
        if (!tableData || tableData.length === 0) {
            toast.error("데이터가 없습니다.");
            return false;
        }

        const excelHandler = {
            getExcelData() {
                const columnTitleFirst = props.loginBranchInfo
                    ? ["No", "원", "반", "계정", "로그인", "로그인", "다운로드", "다운로드", "실행", "실행"]
                    : ["No", "지사", "원", "반", "계정", "로그인", "로그인", "다운로드", "다운로드", "실행", "실행"];
                const columnTitle = [columnTitleFirst, ["최종 로그인", "등급", "최근 다운로드", "등급", "최근 실행", "등급"]];
                const excelExportData = tableData;
                if (searchParam.sort_col) {
                    excelExportData.sort((a, b) => {
                        if (a[searchParam.sort_col] < b[searchParam.sort_col]) {
                            return searchParam.sort == "asc" ? -1 : 1;
                        } else if (a[searchParam.sort_col] > b[searchParam.sort_col]) {
                            return searchParam.sort == "asc" ? 1 : -1;
                        } else {
                            return 0;
                        }
                    });
                }
                const excelDataList = excelExportData
                    ? excelExportData.map((s, idx) => {
                          return props.loginBranchInfo
                              ? [
                                    idx + 1,
                                    s.nurseryName,
                                    s.className,
                                    s.userId,
                                    getFormattedLast(s.loginLast),
                                    getRateName(s.loginRate),
                                    getFormattedLast(s.downLast),
                                    getRateName(s.downRate),
                                    getFormattedLast(s.playLast),
                                    getRateName(s.playRate),
                                ]
                              : [
                                    idx + 1,
                                    s.branchName,
                                    s.nurseryName,
                                    s.className,
                                    s.userId,
                                    getFormattedLast(s.loginLast),
                                    getRateName(s.loginRate),
                                    getFormattedLast(s.downLast),
                                    getRateName(s.downRate),
                                    getFormattedLast(s.playLast),
                                    getRateName(s.playRate),
                                ];
                      })
                    : [];
                const excelData = {
                    fileName: `토모노트사용통계` + "_" + format(new Date(), "yyyyMMddHHmmss") + ".xlsx",
                    sheets: [
                        {
                            name: "토모노트사용통계",
                            columnTitle: columnTitle,
                            data: excelDataList,
                        },
                    ],
                };
                return excelData;
            },
        };
        var wb = XLSX.utils.book_new();
        var excelData = excelHandler.getExcelData();
        excelData.sheets.forEach((s) => {
            var ws = XLSX.utils.aoa_to_sheet([]);
            ws["!merges"] = props.loginBranchInfo
                ? [
                      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // no
                      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // nursery
                      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // class
                      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // user
                      { s: { r: 0, c: 4 }, e: { r: 0, c: 5 } }, // login
                      { s: { r: 0, c: 6 }, e: { r: 0, c: 7 } }, // down
                      { s: { r: 0, c: 8 }, e: { r: 0, c: 9 } }, // play
                  ]
                : [
                      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // no
                      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // branch
                      { s: { r: 0, c: 2 }, e: { r: 1, c: 2 } }, // nursery
                      { s: { r: 0, c: 3 }, e: { r: 1, c: 3 } }, // class
                      { s: { r: 0, c: 4 }, e: { r: 1, c: 4 } }, // user
                      { s: { r: 0, c: 5 }, e: { r: 0, c: 6 } }, // login
                      { s: { r: 0, c: 7 }, e: { r: 0, c: 8 } }, // down
                      { s: { r: 0, c: 9 }, e: { r: 0, c: 10 } }, // play
                  ];
            XLSX.utils.sheet_add_aoa(ws, [excelData.sheets[0].columnTitle[0]], { origin: { r: 0, c: 0 } });
            XLSX.utils.sheet_add_aoa(ws, [excelData.sheets[0].columnTitle[1]], { origin: { r: 1, c: props.loginBranchInfo ? 4 : 5 } });
            XLSX.utils.sheet_add_json(ws, s.data, { origin: { r: 2, c: 0 }, skipHeader: true });

            XLSX.utils.book_append_sheet(wb, ws, s.name);
        });

        var wbout = XLSX.write(wb, { bookType: "xlsx", type: "binary" });
        utility.download2Excel(wbout, excelData.fileName);
    };
    const cellRendererLast = (param) => {
        return getFormattedLast(param.value);
    };
    const cellRendererRate = (param) => {
        const rate = RATE_LIST.find((r) => r.RATE === param.value);
        return `<span style="color: ${rate ? rate.COLOR : ""}">${rate ? rate.NAME : "-"}</span>`;
    };
    const defaultColDef = {
        headerClass: "bg-dark text-light text-right",
        sortable: true,
        resizable: true,
        cellClass: "text-center",
        filter: false,
    };
    const listColumn = [
        {
            headerName: "",
            headerClass: "bg-dark text-light",
            children: [
                {
                    headerName: "No",
                    field: "id",
                    sortable: false,
                    width: 80,
                    minWidth: 50,
                    filter: false,
                    valueGetter: "node.rowIndex + 1",
                },
                {
                    headerName: "지사",
                    field: "branchName",
                    cellClass: "text-left",
                    // pinned: 'left',
                    width: 140,
                    minWidth: 120,
                    hide: props.loginBranchInfo || props.selectedBranch,
                },
                {
                    headerName: "원",
                    field: "nurseryName",
                    cellClass: "text-left",
                    minWidth: 120,
                },
                {
                    headerName: "반",
                    field: "className",
                    cellClass: "text-left",
                    width: 120,
                    minWidth: 120,
                },
                {
                    headerName: "계정",
                    field: "userId",
                    cellClass: "text-left",
                    width: 120,
                    minWidth: 120,
                },
            ],
        },
        {
            headerName: "로그인",
            headerClass: "bg-dark text-light",
            cellClass: "text-center",
            children: [
                {
                    headerName: "최종 로그인",
                    cellClass: "text-center",
                    field: "loginLast",
                    width: 110,
                    minWidth: 110,
                    cellRenderer: cellRendererLast,
                },
                {
                    headerName: "등급",
                    field: "loginRate",
                    cellClass: "text-center",
                    width: 55,
                    minWidth: 55,
                    cellRenderer: cellRendererRate,
                },
            ],
        },
        {
            headerName: "다운로드",
            headerClass: "bg-dark text-light",
            cellClass: "text-center",
            children: [
                {
                    headerName: "최근 다운로드",
                    field: "downLast",
                    cellClass: "text-center",
                    width: 120,
                    minWidth: 120,
                    cellRenderer: cellRendererLast,
                },
                {
                    headerName: "등급",
                    field: "downRate",
                    cellClass: "text-center",
                    width: 55,
                    minWidth: 55,
                    cellRenderer: cellRendererRate,
                },
            ],
        },
        {
            headerName: "실행",
            headerClass: "bg-dark text-light",
            cellClass: "text-center",
            children: [
                {
                    headerName: "최근 실행",
                    field: "playLast",
                    cellClass: "text-center",
                    width: 110,
                    minWidth: 110,
                    cellRenderer: cellRendererLast,
                },
                {
                    headerName: "등급",
                    field: "playRate",
                    cellClass: "text-center",
                    width: 55,
                    minWidth: 55,
                    cellRenderer: cellRendererRate,
                },
            ],
        },
    ];
    return (
        <>
            <div className="row align-items-end">
                <div className="col-xl-2 col-lg-2 col-md-2 col-sm-12 col-12 pl-4">
                    <h5 className="mb-0" style={{ alignSelf: "end" }}>
                        {props.loadingData && <span className="fa fa-spinner"></span>}
                        {!props.loadingData && (
                            <span>
                                {tableData ? tableData.length.toLocaleString() : "0"} / {props.data && props.data.total ? props.data.total.toLocaleString() : "0"}
                            </span>
                        )}
                    </h5>
                </div>
                <div className="col-xl-6 col-lg-7 col-md-5 col-sm-12 col-12 text-right">
                    <span className="">
                        <input style={{ display: "inline" }} id="filterAll" type="checkbox" checked={searchFilter.all} onChange={(e) => onChangeFilter(e)} value="all" />
                        <label htmlFor="filterAll" className="ml-1">
                            전체 보기
                        </label>
                    </span>
                </div>
                <div className="col-xl-4 col-lg-3 col-md-5 col-sm-12 col-12 text-right">
                    <InputGroup className="input-group my-group">
                        <SearchField handleKeyPress={handleKeyPress} ref={searchInputRef} />
                        <InputGroup.Append>
                            <input type="submit" className="btn btn-primary" value="검색" onClick={onSearch} />
                        </InputGroup.Append>
                    </InputGroup>
                </div>
            </div>
            <div className="row">
                <div className="col">
                    <AgGridReactEx
                        defaultColDef={defaultColDef}
                        columnDefs={listColumn}
                        rowData={tableData}
                        onRowDataChanged={onTableDataChanged}
                        rowSelection="single"
                        enableCellTextSelection={true}
                        pagination={true}
                        onGridReady={onGridReady}
                        onSortChanged={onSortChanged}
                        overlayNoRowsTemplate={`<span>목록이 없습니다.</span>`}
                        suppressSizeColumnsToFit={false}
                        excelDownload={true}
                        excelDataHandler={saveTableXlsx}
                    />
                </div>
            </div>
        </>
    );
});

export { RATE_LIST, PieRateTotal, PieRateList, PieRate, ClassList };
